'use client';

import type { CategoriesEntity } from '@eliadmin/core/src/api/general/types/general-layout-types';

import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from '@eliadmin/core/src/components/ui/sheet';
import { HEADER_CONFIG } from '@eliadmin/core/src/constants/header-config';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';

import { IoClose } from 'react-icons/io5';
import { MobileSidebarDesktopContent } from './mobile-sidebar-desktop-content';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@eliadmin/core/src/components/ui/accordion';

import Link from '@eliadmin/core/src/components/link';
import { Separator } from '@eliadmin/core/src/components/ui/separator';
import { FiMapPin } from 'react-icons/fi';
import { SocialIcons } from '@eliadmin/core/src/components/header/social-icons';

const CategoryItem = ({ category, layer }: { category: CategoriesEntity; layer?: number }) => {
  const hasChildren = !!category.childs?.length;

  return (
    <AccordionItem
      className={cn('flex flex-col', layer && 'ml-4 w-full border-none')}
      value={category.slug + layer?.toString()}
    >
      <li
        className={cn(
          'flex w-full items-center justify-between border-none bg-transparent px-4 py-2 text-foreground hover:bg-primary hover:text-white',
          'ea-menu-sidebar__list-item',
          layer === 1 && 'ea-sheet-close__layer-1',
          // className?.listItem,
        )}
      >
        <SheetClose
          asChild
          className={cn(
            'font-heading text-2xl font-medium uppercase',
            layer === 1 && 'text-xl font-medium',
            layer && layer > 1 && 'text-base font-normal',
            category.name === 'Akciová ponuka' && 'text-primary hover:text-white',
          )}
        >
          <Link prefetchOnHover href={category.slug ?? '#'} className="flex-1" prefetch={false}>
            {category.name}
          </Link>
        </SheetClose>
        {hasChildren && (
          <AccordionTrigger className="h-5 w-5" iconstyles="w-5 h-5 text-primary flex-1" />
        )}
      </li>
      <AccordionContent className="pb-0 ">
        <Accordion type="single" collapsible>
          {hasChildren &&
            category.childs?.map((child, index) => (
              <CategoryItem key={index} category={child} layer={layer ? layer + 1 : 1} />
            ))}
        </Accordion>
      </AccordionContent>
    </AccordionItem>
  );
};

export type MobileSidebarProps = {
  className?: {
    trigger?: string;
    content?: string;
    firstSeparator?: string;
    menuList?: string;
    menuLink?: string;
    menuGroup?: string;
    menuSeparator?: string;
    buttonWrapper?: string;
    button?: string;
    iconsWrapper?: string;
    icon?: string;
  };
  side?: 'right' | 'left' | 'bottom' | 'top';
  firstSeparator?: boolean;
  menu?: {
    type: 'page';
    name: string;
    path?: string;
  }[];
  menuArrow?: boolean;
  menuSeparator?: boolean;
  categories: CategoriesEntity[];
};

const MobileSidebar = (props: MobileSidebarProps) => {
  const generalLayout = useGlobalStore(state => state.generalLayout);

  const categories = props.categories;

  return (
    <>
      <Sheet>
        <SheetTrigger aria-label="Menu"
          className={cn(
            'flex items-start justify-start font-heading text-base font-medium uppercase text-foreground sm:w-[192px] sm:text-lg lg:text-xl',
            'ea-mobile-sidebar__trigger',
            props.className?.trigger,
          )}
        >
          <div className="flex items-center justify-center gap-1.5 sm:gap-2 lg:gap-4">
            {HEADER_CONFIG?.sidebar?.triggerIcon} MENU
          </div>
          <span className="hidden items-center justify-center gap-1.5 sm:gap-2 lg:gap-4">
            <IoClose size={24} /> MENU
          </span>
        </SheetTrigger>
        <SheetContent
          side={'left'}
          className={cn(
            'top-[83px] z-[53] flex h-full w-full flex-col items-start justify-start gap-6 border-none px-6 py-8 shadow-none sm:top-[95px] md:top-[96px] md:h-fit md:w-screen md:flex-row md:items-center  md:justify-center md:gap-0 lg:top-[112px]',
            'ea-mobile-sidebar__content',
          )}
        >
          <SheetTitle className="sr-only">Menu</SheetTitle>
          <MobileSidebarDesktopContent categories={categories} />
          <ul className="flex w-full flex-col items-start justify-start gap-4 md:hidden">
            {categories.map(category => (
              <SheetClose key={category.slug} asChild>
                <Link prefetchOnHover
                  prefetch={false}
                  href={category.slug}
                  className="font-heading text-2xl font-medium uppercase text-foreground"
                >
                  {category.name}
                </Link>
              </SheetClose>
            ))}
          </ul>
          <Separator orientation="horizontal" className="bg-border md:hidden" />
          <h3 className="w-full max-w-[220px] font-heading text-xl font-medium uppercase text-foreground md:hidden">
            NAVŠTÍVTE NAŠU KAMENNÚ PREDAJŇU
          </h3>
          <div className="flex items-center justify-start gap-2 text-base text-primary md:hidden">
            <FiMapPin size={20} /> {generalLayout.contact?.address}, {generalLayout.contact?.city}
          </div>
          <div className="flex w-full flex-col items-start justify-start gap-1 md:hidden">
            <p className="text-base font-normal text-[#969696]">Otváracie hodiny</p>
            <p className="text-foreground">Pondelok-Piatok: 10:00 - 17:00</p>
            <p className="text-foreground">Sobota: 09:00 - 12:00</p>
          </div>
          <Separator orientation="horizontal" className="bg-border md:hidden" />
          <SocialIcons className={{ wrapper: 'md:hidden' }} />
        </SheetContent>
      </Sheet>
    </>
  );
};

export default MobileSidebar;
